import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Flame, Award, Trophy, Gamepad2, Film, Star, BookOpen, Users, MapPin, Globe, Calendar, Gift } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { API_URL } from '../config/api';

const BADGE_EMOJIS = {
  gamepad: '\ud83c\udfae', flame: '\ud83d\udd25', target: '\ud83c\udfaf', trophy: '\ud83c\udfc6',
  swords: '\u2694\ufe0f', film: '\ud83c\udfac', clapperboard: '\ud83c\udfac', crown: '\ud83d\udc51',
  pen: '\u270f\ufe0f', users: '\ud83d\udc65', book: '\ud83d\udcd6', star: '\u2b50',
  tv: '\ud83d\udcfa', skull: '\ud83d\udc80', rocket: '\ud83d\ude80', laugh: '\ud83d\ude02',
  theater: '\ud83c\udfad', zap: '\u26a1', globe: '\ud83c\udf0d',
  sofa: '\ud83d\udecb\ufe0f', flashlight: '\ud83d\udd26', flask: '\u2697\ufe0f', dragon: '\ud83d\udc09',
  heart: '\u2764\ufe0f', zombie: '\ud83e\udddf', mirror: '\ud83e\ude9e', castle: '\ud83c\udff0',
  wind: '\ud83c\udf2c\ufe0f', medal: '\ud83c\udf96\ufe0f',
};

const BadgeMini = ({ badge }) => {
  const emoji = BADGE_EMOJIS[badge.icon] || '\ud83c\udfc5';
  return (
    <div
      className={`relative flex flex-col items-center gap-1 p-2.5 rounded-lg transition-all ${
        badge.earned
          ? 'bg-zinc-800 border border-amber-800/30'
          : 'bg-zinc-800/40 border border-zinc-700/40 opacity-40'
      }`}
      title={badge.description}
      data-testid={`profile-badge-${badge.id}`}
    >
      <span className={`text-xl ${badge.earned ? '' : 'grayscale'}`}>{emoji}</span>
      <span className={`text-[10px] leading-tight text-center font-medium ${badge.earned ? 'text-white' : 'text-gray-500'}`}>{badge.name}</span>
      {badge.target > 0 && !badge.earned && (
        <div className="w-full h-1 bg-zinc-700 rounded-full mt-0.5 overflow-hidden">
          <div className="h-full bg-purple-500 rounded-full" style={{ width: `${Math.min(((badge.progress || 0) / badge.target) * 100, 100)}%` }} />
        </div>
      )}
    </div>
  );
};

const UserProfile = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, badgesRes, challengeRes, prizesRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard-stats/${user.id}`),
        axios.get(`${API_URL}/api/badges/${user.id}`),
        axios.get(`${API_URL}/api/genre-challenges/${user.id}`),
        axios.get(`${API_URL}/api/prizes/my?user_id=${user.id}`),
      ]);
      setStats(statsRes.data);
      setBadges(badgesRes.data);
      setChallenges(challengeRes.data || []);
      setPrizes(prizesRes.data.prizes || []);
    } catch (err) {
      console.error('Failed to load profile data:', err);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const totalBadgesEarned = badges?.total_earned || 0;
  const totalBadgesAvail = badges?.total_available || 30;
  const gameBadges = badges?.game_badges || [];
  const movieBadges = badges?.movie_badges || [];
  const genreBadges = badges?.genre_badges || [];
  const earnedBadges = [...gameBadges, ...movieBadges, ...genreBadges].filter(b => b.earned);
  const completedChallenges = challenges.filter(c => c.completed).length;

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'badges', label: `Badges (${totalBadgesEarned})` },
    { key: 'prizes', label: `Prizes${prizes.length ? ` (${prizes.length})` : ''}` },
    { key: 'challenges', label: `Challenges (${completedChallenges}/${challenges.length})` },
  ];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="user-profile-page">
      {/* Cover / Header */}
      <div className="relative">
        <div className="h-40 sm:h-52 bg-gradient-to-br from-zinc-900 via-zinc-800 to-amber-950" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="relative -mt-14 sm:-mt-16 flex items-end gap-4 sm:gap-5">
            {/* Avatar */}
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 border-black bg-zinc-800 flex items-center justify-center text-2xl sm:text-3xl font-bold text-white overflow-hidden flex-shrink-0" data-testid="profile-avatar">
              {user.profile_picture ? (
                <img src={user.profile_picture} alt="" className="w-full h-full object-cover" />
              ) : (
                `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
              )}
            </div>
            <div className="pb-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold truncate" data-testid="profile-name">
                {user.display_name || `${user.first_name} ${user.last_name}`}
              </h1>
              {user.username && <p className="text-gray-400 text-sm">@{user.username}</p>}
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                {user.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{user.location}</span>}
                {user.website && <a href={user.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-400 hover:underline"><Globe className="h-3 w-3" />{user.website.replace(/https?:\/\//, '')}</a>}
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Joined {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
              </div>
            </div>
          </div>
          {user.bio && <p className="text-gray-300 text-sm mt-3 max-w-xl">{user.bio}</p>}

          {/* Back button */}
          <button onClick={() => navigate('/dashboard')} className="absolute top-4 left-4 p-2 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur" data-testid="profile-back-btn">
            <ArrowLeft className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 mt-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="profile-stats-grid">
          <div className="bg-gradient-to-br from-amber-900/30 to-zinc-800 rounded-xl p-4 border border-amber-800/30 text-center">
            <Flame className="h-5 w-5 text-amber-400 mx-auto mb-1" />
            <p className="text-2xl sm:text-3xl font-black text-amber-400">{stats?.casita_points ?? 0}</p>
            <p className="text-[11px] text-gray-400 font-medium">CasitaPoints</p>
          </div>
          <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700 text-center">
            <Trophy className="h-5 w-5 text-amber-500 mx-auto mb-1" />
            <p className="text-2xl sm:text-3xl font-black text-white">#{stats?.rank ?? '—'}</p>
            <p className="text-[11px] text-gray-400 font-medium">Global Rank</p>
          </div>
          <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700 text-center">
            <Award className="h-5 w-5 text-purple-400 mx-auto mb-1" />
            <p className="text-2xl sm:text-3xl font-black text-purple-400">{totalBadgesEarned}</p>
            <p className="text-[11px] text-gray-400 font-medium">Badges Earned</p>
          </div>
          <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700 text-center">
            <Gamepad2 className="h-5 w-5 text-indigo-400 mx-auto mb-1" />
            <p className="text-2xl sm:text-3xl font-black text-white">{stats?.total_played ?? 0}</p>
            <p className="text-[11px] text-gray-400 font-medium">Games Played</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 mt-6">
        <div className="flex border-b border-zinc-800">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.key ? 'border-amber-500 text-amber-400' : 'border-transparent text-gray-400 hover:text-white'
              }`}
              data-testid={`profile-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 pb-20">
        {/* === OVERVIEW === */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Recent earned badges */}
            {earnedBadges.length > 0 && (
              <div>
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Award className="h-4 w-4 text-purple-400" />Earned Badges</h3>
                <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                  {earnedBadges.map(b => <BadgeMini key={b.id} badge={b} />)}
                </div>
              </div>
            )}

            {/* Game Stats */}
            <div>
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Gamepad2 className="h-4 w-4 text-indigo-400" />Film in Five Stats</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { val: stats?.total_played ?? 0, label: 'Played', color: 'text-white' },
                  { val: stats?.total_game_score ?? 0, label: 'Total Pts', color: 'text-amber-400' },
                  { val: stats?.best_score ?? 0, label: 'Best Score', color: 'text-green-400' },
                  { val: stats?.streak ?? 0, label: 'Streak', color: 'text-purple-400' },
                ].map((s, i) => (
                  <div key={i} className="bg-zinc-800 rounded-lg p-3 text-center">
                    <p className={`text-xl font-bold ${s.color}`}>{s.val}</p>
                    <p className="text-[11px] text-gray-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Challenges */}
            {challenges.filter(c => !c.completed).length > 0 && (
              <div>
                <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Star className="h-4 w-4 text-amber-500" />Active Challenges</h3>
                <div className="space-y-2">
                  {challenges.filter(c => !c.completed).slice(0, 5).map(ch => (
                    <div key={ch.id} className="bg-zinc-800 rounded-lg p-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{ch.name}</p>
                        <p className="text-gray-500 text-xs">{ch.description} &middot; {ch.points_reward} pts</p>
                      </div>
                      <div className="text-right flex-shrink-0 w-20">
                        <p className="text-xs text-gray-400 mb-1">{ch.progress}/{ch.target}</p>
                        <div className="w-full h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(ch.progress / ch.target) * 100}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* === BADGES === */}
        {activeTab === 'badges' && (
          <div className="space-y-6">
            {[
              { title: 'Game Badges', icon: Gamepad2, list: gameBadges, color: 'text-amber-400' },
              { title: 'Movie Badges', icon: Film, list: movieBadges, color: 'text-red-400' },
              { title: 'Genre Badges', icon: BookOpen, list: genreBadges, color: 'text-purple-400' },
            ].map(section => (
              <div key={section.title}>
                <h3 className={`font-semibold text-sm mb-3 flex items-center gap-2 ${section.color}`}>
                  <section.icon className="h-4 w-4" />{section.title}
                  <span className="text-gray-500 text-xs font-normal ml-1">
                    ({section.list.filter(b => b.earned).length}/{section.list.length})
                  </span>
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2">
                  {section.list.map(b => <BadgeMini key={b.id} badge={b} />)}
                </div>
              </div>
            ))}
          </div>
        )}


        {/* === MY PRIZES === */}
        {activeTab === 'prizes' && (
          <div className="space-y-3" data-testid="my-prizes-section">
            {prizes.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <Gift className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No prizes yet</p>
                <p className="text-xs text-gray-600 mt-1">Keep playing and climbing the leaderboard!</p>
              </div>
            ) : prizes.map(p => {
              const placementColors = { '1ST': 'from-amber-600 to-yellow-500', '2ND': 'from-gray-400 to-gray-300', '3RD': 'from-orange-600 to-amber-500' };
              const placementLabels = { '1ST': '1st Place', '2ND': '2nd Place', '3RD': '3rd Place' };
              const statusLabels = { 'AWARDED': 'Awarded', 'SENT': 'Sent', 'CLAIMED': 'Claimed' };
              const statusColors = { 'AWARDED': 'bg-blue-900/30 text-blue-400', 'SENT': 'bg-emerald-900/30 text-emerald-400', 'CLAIMED': 'bg-purple-900/30 text-purple-400' };
              return (
                <div key={p.id} className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden" data-testid={`prize-${p.id}`}>
                  <div className={`bg-gradient-to-r ${placementColors[p.placement] || 'from-zinc-600 to-zinc-500'} px-4 py-2 flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-white" />
                      <span className="text-sm font-bold text-white">{placementLabels[p.placement] || p.placement}</span>
                    </div>
                    <span className="text-xs text-white/80 font-medium">{p.month}</span>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-semibold">{p.prize_type}</p>
                        <p className="text-gray-500 text-xs">Code: {p.gift_card_id}{p.gift_card_pin ? ` | PIN: ${p.gift_card_pin}` : ''}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 text-lg font-bold">${p.gift_card_value}</p>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${statusColors[p.status] || 'bg-zinc-700 text-gray-400'}`}>{statusLabels[p.status] || p.status}</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-600">Awarded {new Date(p.awarded_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* === CHALLENGES === */}
        {activeTab === 'challenges' && (
          <div className="space-y-3">
            {challenges.map(ch => (
              <div
                key={ch.id}
                className={`rounded-lg p-4 flex items-center gap-3 ${
                  ch.completed ? 'bg-green-900/20 border border-green-800/40' : 'bg-zinc-800 border border-zinc-700'
                }`}
                data-testid={`challenge-${ch.id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${ch.completed ? 'bg-green-700/30 text-green-400' : 'bg-zinc-700 text-gray-400'}`}>
                  {ch.completed ? <Trophy className="h-5 w-5" /> : <Star className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${ch.completed ? 'text-green-400' : 'text-white'}`}>{ch.name}</p>
                  <p className="text-gray-500 text-xs">{ch.description}</p>
                </div>
                <div className="text-right flex-shrink-0 w-24">
                  <p className="text-xs text-amber-400 font-semibold">{ch.points_reward} pts</p>
                  <p className="text-xs text-gray-400 mt-0.5 mb-1">{ch.progress}/{ch.target}</p>
                  <div className="w-full h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${ch.completed ? 'bg-green-500' : 'bg-amber-500'}`}
                      style={{ width: `${Math.min((ch.progress / ch.target) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
